import {
  placeableCategoryIds,
  PRODUCT_STATUSES,
  productStatusSchema,
  type Category,
  type ProductStatus,
} from '@avrahami/contracts';
import {
  Button,
  Card,
  CheckboxField,
  SelectField,
  TextAreaField,
  TextField,
  toggleValue,
} from '@avrahami/ui';
import { he } from '../i18n/he';
import { categoryTree } from '../lib/categories';
import { costChange, type ProductDraft, type SpecDraft } from '../lib/drafts';
import type { FieldErrors } from '../lib/forms';
import { formatProfit, profitOf, shekelsToMinor } from '../lib/money';
import { MoneyField } from './fields';

/** Rows of a specification (label → value), with add and remove. */
function SpecRows({
  title,
  rows,
  onChange,
  error,
  testId,
}: {
  title: string;
  rows: readonly SpecDraft[];
  onChange: (rows: SpecDraft[]) => void;
  error: string | undefined;
  testId: string;
}) {
  const update = (index: number, change: Partial<SpecDraft>) => {
    onChange(rows.map((row, at) => (at === index ? { ...row, ...change } : row)));
  };
  return (
    <fieldset className="flex flex-col gap-2" data-testid={testId}>
      <legend className="mb-1 text-sm font-medium text-slate-700">{title}</legend>
      {rows.map((row, index) => (
        <div key={index} className="flex flex-wrap items-end gap-2">
          <TextField
            label={`${he.products.specLabel} ${String(index + 1)}`}
            value={row.label}
            maxLength={120}
            className="w-48"
            onChange={(event) => {
              update(index, { label: event.target.value });
            }}
          />
          <TextField
            label={`${he.products.specValue} ${String(index + 1)}`}
            value={row.value}
            maxLength={500}
            className="min-w-48 flex-1"
            onChange={(event) => {
              update(index, { value: event.target.value });
            }}
          />
          <Button
            tone="secondary"
            onClick={() => {
              onChange(rows.filter((_row, at) => at !== index));
            }}
          >
            {he.products.removeSpec}
          </Button>
        </div>
      ))}
      <div>
        <Button
          tone="secondary"
          onClick={() => {
            onChange([...rows, { label: '', value: '' }]);
          }}
        >
          {he.products.addSpec}
        </Button>
      </div>
      {error === undefined ? null : (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </fieldset>
  );
}

/** The first message under a path (`facts.specs` covers `facts.specs.2.label`). */
function errorUnder(errors: FieldErrors, prefix: string): string | undefined {
  const key = Object.keys(errors).find((path) => path === prefix || path.startsWith(`${prefix}.`));
  return key === undefined ? undefined : errors[key];
}

/**
 * Where the product is placed. A product belongs to sub-categories only, so a main category is a
 * heading here and never a choice. A placement from before that rule (the product sits in a main
 * category) stays ticked, and can only be removed.
 */
function CategoryPicker({
  categories,
  chosen,
  onChange,
}: {
  categories: readonly Category[];
  chosen: readonly string[];
  onChange: (categoryIds: string[]) => void;
}) {
  const placeable = placeableCategoryIds(categories);
  if (placeable.size === 0 && chosen.length === 0) {
    return <p className="text-sm text-slate-500">{he.products.noCategories}</p>;
  }
  return (
    <fieldset
      className="flex max-h-80 flex-col gap-1 overflow-y-auto"
      data-testid="product-categories"
    >
      <legend className="mb-2 text-xs text-slate-500">{he.products.categoriesHint}</legend>
      {categoryTree(categories).map(({ category, depth }) => {
        const indent = { paddingInlineStart: `${String(depth * 1.25)}rem` };
        const isChosen = chosen.includes(category.id);
        if (!placeable.has(category.id) && !isChosen) {
          return (
            <p
              key={category.id}
              style={indent}
              data-testid="category-heading"
              className="mt-2 text-sm font-semibold text-slate-900 first:mt-0"
            >
              {category.name}
            </p>
          );
        }
        return (
          <div key={category.id} style={indent}>
            <CheckboxField
              label={category.name}
              {...(placeable.has(category.id) ? {} : { hint: he.products.mainCategory })}
              checked={isChosen}
              onChange={() => {
                onChange(toggleValue(chosen, category.id));
              }}
            />
          </div>
        );
      })}
    </fieldset>
  );
}

/**
 * The product form, shared by "new product" and the edit page. Catalog products (`locked`) show
 * their copied facts read-only; the store adds its own specification rows.
 *
 * A new product gets its prices and sub-categories here (`categories`). The edit page has neither:
 * prices are edited in the product list, and the store never moves a product to another category.
 */
export function ProductForm({
  draft,
  set,
  errors,
  locked,
  facts,
  categories,
  statuses = PRODUCT_STATUSES,
}: {
  draft: ProductDraft;
  set: (change: Partial<ProductDraft>) => void;
  errors: FieldErrors;
  locked: boolean;
  /** The copied facts to show when `locked`. */
  facts?: { label: string; value: string }[];
  /** A new product only: where it may be placed. */
  categories?: readonly Category[];
  statuses?: readonly ProductStatus[];
}) {
  const creating = categories !== undefined;
  const profit = profitOf(shekelsToMinor(draft.cost), shekelsToMinor(draft.price));
  const statusField = (
    <SelectField
      label={he.products.statusField}
      name="status"
      data-testid="product-status-select"
      value={draft.status}
      error={errors['status']}
      onChange={(event) => {
        set({ status: productStatusSchema.parse(event.target.value) });
      }}
    >
      {statuses.map((status) => (
        <option key={status} value={status}>
          {he.productStatus[status]}
        </option>
      ))}
    </SelectField>
  );
  return (
    <div className="flex flex-col gap-6">
      {creating ? (
        <Card title={he.products.pricing}>
          <div className="grid gap-4 sm:grid-cols-3">
            <MoneyField
              label={he.products.costField}
              name="cost"
              value={draft.cost}
              hint={
                !locked
                  ? he.products.costHint
                  : draft.cost === ''
                    ? he.products.costMissingHint
                    : he.products.costLockedHint
              }
              disabled={locked}
              error={errors['price.costMinor']}
              onChange={(cost) => {
                set(costChange(draft, cost));
              }}
            />
            <MoneyField
              label={he.products.priceField}
              name="price"
              value={draft.price}
              hint={he.products.priceHint}
              error={errors['price.amountMinor']}
              onChange={(price) => {
                set({ price });
              }}
            />
            <TextField
              label={he.products.profitField}
              name="profit"
              readOnly
              tabIndex={-1}
              hint={he.products.profitHint}
              value={profit === null ? he.common.none : formatProfit(profit)}
            />
            <MoneyField
              label={he.products.compareAt}
              name="compareAt"
              value={draft.compareAt}
              hint={he.products.compareAtHint}
              error={errors['price.compareAtMinor']}
              onChange={(compareAt) => {
                set({ compareAt });
              }}
            />
            {statusField}
          </div>
        </Card>
      ) : (
        <Card title={he.products.statusField}>
          <div className="max-w-xs">{statusField}</div>
          <p className="mt-3 text-xs text-slate-500">{he.products.pricesInList}</p>
        </Card>
      )}

      <Card title={he.products.facts}>
        <div className="flex flex-col gap-4">
          {locked ? (
            <>
              <p className="text-sm text-slate-600">{he.products.factsLocked}</p>
              <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
                {(facts ?? []).map((row, index) => (
                  <div key={`${row.label}-${String(index)}`} className="contents">
                    <dt className="text-slate-500">{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            </>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  label={he.products.factName}
                  name="name"
                  required
                  maxLength={200}
                  value={draft.name}
                  error={errors['facts.name']}
                  onChange={(event) => {
                    set({ name: event.target.value });
                  }}
                />
                <TextField
                  label={he.products.factSku}
                  name="sku"
                  required
                  maxLength={50}
                  dir="ltr"
                  value={draft.sku}
                  error={errors['facts.sku']}
                  onChange={(event) => {
                    set({ sku: event.target.value });
                  }}
                />
                <TextField
                  label={he.products.barcode}
                  name="barcode"
                  maxLength={64}
                  dir="ltr"
                  value={draft.barcode}
                  error={errors['facts.barcode']}
                  onChange={(event) => {
                    set({ barcode: event.target.value });
                  }}
                />
                <TextField
                  label={he.products.model}
                  name="model"
                  maxLength={100}
                  value={draft.model}
                  error={errors['facts.model']}
                  onChange={(event) => {
                    set({ model: event.target.value });
                  }}
                />
                <TextField
                  label={he.products.manufacturer}
                  name="manufacturer"
                  maxLength={100}
                  value={draft.manufacturer}
                  error={errors['facts.manufacturer']}
                  onChange={(event) => {
                    set({ manufacturer: event.target.value });
                  }}
                />
              </div>
              <SpecRows
                title={he.products.specs}
                rows={draft.specs}
                testId="product-specs"
                error={errorUnder(errors, 'facts.specs')}
                onChange={(specs) => {
                  set({ specs });
                }}
              />
            </>
          )}
          {locked ? (
            <SpecRows
              title={he.products.extraSpecs}
              rows={draft.extraSpecs}
              testId="product-extra-specs"
              error={errorUnder(errors, 'facts.extraSpecs')}
              onChange={(extraSpecs) => {
                set({ extraSpecs });
              }}
            />
          ) : null}
        </div>
      </Card>

      <Card title={he.products.content}>
        <div className="flex flex-col gap-4">
          <TextField
            label={he.products.titleField}
            name="title"
            maxLength={200}
            value={draft.title}
            error={errors['content.title']}
            onChange={(event) => {
              set({ title: event.target.value });
            }}
          />
          <TextAreaField
            label={he.products.description}
            name="description"
            rows={8}
            maxLength={20_000}
            value={draft.description}
            error={errors['content.description']}
            onChange={(event) => {
              set({ description: event.target.value });
            }}
          />
          <TextField
            label={he.products.seoTitle}
            name="seoTitle"
            maxLength={70}
            value={draft.seoTitle}
            error={errors['content.seoTitle']}
            onChange={(event) => {
              set({ seoTitle: event.target.value });
            }}
          />
          <TextAreaField
            label={he.products.seoDescription}
            name="seoDescription"
            rows={3}
            maxLength={320}
            value={draft.seoDescription}
            error={errors['content.seoDescription']}
            onChange={(event) => {
              set({ seoDescription: event.target.value });
            }}
          />
        </div>
      </Card>

      {creating ? (
        <Card title={he.products.categories}>
          <CategoryPicker
            categories={categories}
            chosen={draft.categoryIds}
            onChange={(categoryIds) => {
              set({ categoryIds });
            }}
          />
          {errorUnder(errors, 'categoryIds') === undefined ? null : (
            <p role="alert" className="mt-2 text-xs text-red-600">
              {errorUnder(errors, 'categoryIds')}
            </p>
          )}
        </Card>
      ) : null}
    </div>
  );
}
